/* eslint-disable react/static-property-placement */
/* eslint-disable react/forbid-prop-types */
import React, { useContext, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ScrollLock from 'react-scrolllock';
import { Scrollbars } from 'react-custom-scrollbars';
import Raven from 'raven-js';
import compose from 'lodash/flowRight';
import sortBy from 'lodash/sortBy';
import { graphql } from '@apollo/client/react/hoc';
import { analyticService } from 'global/services';

import { VIEW_CUSTOMIZATION_COMMENT } from 'app/graphql/network/customizationComment';
import { ADD_ITEMS_TO_CART_BY_PERIOD } from 'app/graphql/network/basket/basketMutations';


/* QUERIES AND MUTATIONS */
import {
    CATEGORIES_ORDER,
    CATEGORY_MOST_INTRESTING,
    CATEGORY_MOST_INTRESTING_TAG_ID,
    CATEGORY_TITLES,
    isCategoryVisibleForLocation,
} from 'app/const/categories';
import {
    NY_DISHES,
    PROTEINS_ID,
    STYLE_ID,
    INGREDIENTS_ID,
    IS_PREMIUM_FILTER_ENABLED,
    PREMIUM_FILTER_TITLE,
    PREMIUM_FILTER_ID,
    BEST_TAG_ID,
    NO_FAT_FILTER,
    NO_FAT_FILTER_TITLE,
    getCategoryCount,
    extendTagByCount,
} from 'app/utils/filters';
import { selectDishes } from 'app/utils/dish';

import connect from 'app/connect';
import { menuDatesState } from 'app/containers/contexts/menuDates.context';
import {
    abTestDataContext, MENU_SORT_DATES, MENU_SORT_DATES_CONTROL, MENU_SORT_DATES_TEST,
} from 'app/containers/contexts/abTest.context';
import { OverlayContextDispatch, withDispatchOverlay } from 'app/containers/contexts/overlay.context';
import { clearAfterSwitchDialogTexts, FiltersContext, setAnoterPeriodHandler } from 'app/containers/contexts/filters.context/filters.context';

import { appendTagReaction, deleteTagReaction, resetSelectedFiltersReaction } from 'app/hooks/useFilterHandlers';
import Responsive from 'app/components/Responsive';
import { LocaleContext, withLocaleContext } from 'app/containers/LocaleProvider';
import PeriodFilter from './components/Period';
import MenuFilters from './components/MenuFilters';
import SubscriptionSwitch from './components/SubscriptionSwitch';

import { locales } from './filters.locales';

import './filters.scss';
import { MenuDataContext } from 'app/containers/contexts/menu.context';
import { useBasketQueryAdapter } from 'app/connect/connectToBasket';
import { subscriptionTypeVar } from 'app/apollo/reaction';
import locale from 'app/utils/locale';
import { useToggleSubscription } from 'app/hooks/useToggleSubscription';
import {
    useCategoryLocationKey, useMenuPeriodsData, useSpecificPeriodData,
} from 'app/hooks/accessors';
import { allNYPeriods } from 'global/const/ny';
import CityFilter from './components/CityFilter';
import {
    setCategoriesReaction,
} from '../../apollo/reactions/filters';
import { useParticipantIdABTest } from '../Menu/Menu/hooks/useParticipantIdABTest';


const isPeriodEmpty = (period) => !period || period === '';

const inRange = (number, start = 0, end = Infinity) => (number >= start && number <= end);

const getScrollTopPx = () => {
    // при изменение поменять в filters.scss
    const widthAndScroll = {
        1280: 146,
        1366: 150,
    };
    const is1280 = inRange(window.innerWidth, 960, 1280);
    const is1366 = inRange(window.innerWidth);
    let result;
    switch (true) {
        case is1280:
            result = widthAndScroll['1280'] + window.scrollY;
            break;
        case is1366:
            result = widthAndScroll['1366'] + window.scrollY;
            break;
        default:
            result = window.scrollY;
            break;
    }
    return result;
};


const mapCategories = (categories, selectedCategories, menuDishes, selectedTagIds, basketDishes, locale, locationKey) => {
    const mapped = categories
        .filter((c) => isCategoryVisibleForLocation(c.id, locationKey))
        .map((c) => {
            const isSelected = selectedCategories.includes(c.id);
            const count = getCategoryCount({
                id: c.id,
                menuDishes,
                selectedTagIds,
                basketDishes,
            });
            const title = CATEGORY_TITLES[locale][c.id] || c.title;
            return {
                ...c,
                title,
                count,
                isSelected,
            };
        });

    const mostIntrestingCategory = {
        id: CATEGORY_MOST_INTRESTING,
        title: CATEGORY_TITLES[locale][CATEGORY_MOST_INTRESTING],
        count: selectDishes(
            menuDishes,
            null,
            [CATEGORY_MOST_INTRESTING_TAG_ID, ...selectedTagIds],
            selectedCategories,
        ).length,
        isSelected: selectedCategories.includes(CATEGORY_MOST_INTRESTING),
    };

    const resultCategories = CATEGORIES_ORDER.includes(CATEGORY_MOST_INTRESTING)
        ? [mostIntrestingCategory, ...mapped]
        : mapped;

    return resultCategories.filter((c) => {
        if (selectedTagIds.length > 0) return true;
        return c.count > 0;
    });
};


const getTagTitle = (tag) => {
    if (tag.id === NO_FAT_FILTER) {
        return NO_FAT_FILTER_TITLE;
    }
    if (tag.id === PREMIUM_FILTER_ID) {
        return PREMIUM_FILTER_TITLE;
    }
    // if (tag.id === '1219') {
    //     return 'Пост';
    // }
    return tag.title;
};


// eslint-disable-next-line arrow-body-style
const mapTags = (tags, categoryId, menuDishes, selectedCategories, selectedTagsIds, basketDishes, canCustomizeMenu) => {
    const mappedTags = tags
        .filter((t) => categoryId.includes(String(t.categoryId)))
        .map((t) => {
            const isSelected = Boolean(selectedTagsIds.find((id) => id === t.id));
            const { count } = extendTagByCount(t, menuDishes, selectedCategories, selectedTagsIds, basketDishes);

            return {
                ...t,
                title: getTagTitle(t),
                count,
                isSelected,
                disabled: !canCustomizeMenu,
            };
        });

    return sortBy(mappedTags, [
        (tag) => tag.id !== PREMIUM_FILTER_ID,
        (tag) => tag.id === BEST_TAG_ID,
    ]);
};


class Filters extends React.PureComponent {
    filtersRef = React.createRef();

    sentinelRef = React.createRef();

    wrapContentRef = React.createRef();

    state = {
        isFullyScrolled: false,
    };

    componentDidMount() {
        document.body.classList.add('mounted');
        this.observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        // this.wrapContentRef.current.style.opacity = 0;
                        setTimeout(
                            () => {
                                if (this.wrapContentRef?.current?.style) {
                                    this.wrapContentRef.current.style.opacity = 1;
                                }
                                this.setState({ isFullyScrolled: true });
                            },
                            150,
                        );
                    } else {
                        // this.wrapContentRef.current.style.opacity = 0;
                        setTimeout(
                            () => {
                                if (this.wrapContentRef?.current?.style) {
                                    this.wrapContentRef.current.style.opacity = 1;
                                }
                                this.setState({ isFullyScrolled: false });
                            },
                            150,
                        );
                    }
                });
            },
            {
                root: null,
                threshold: 0,
            },
        );

        if (this.sentinelRef.current) {
            this.observer.observe(this.sentinelRef.current);
        }
    }

    componentWillUnmount() {
        if (this.observer && this.sentinelRef.current) {
            this.observer.unobserve(this.sentinelRef.current);
        }
    }

    handleCloseFilters = (e) => {
        const filters = this.filtersRef.current;
        if (filters && filters.contains(e.target)) return;

        const { onClose } = this.props;
        onClose();
    };

    handleSetPeriod = (start) => {
        const { setAnotherFilterPeriod } = this.props;
        const {
            basketQuery,
            dispatchOverlayContext: { pushDialogOverlay, closeLastOverlays },
            localeContext: { locale },
        } = this.props;

        const isBasketEmpty = basketQuery.cart.sections.length === 0;
        const isNeededClearBasket = allNYPeriods.includes(start);

        if (isNeededClearBasket && !isBasketEmpty) {
            const dialogData = {
                ...clearAfterSwitchDialogTexts[locale],
                onConfirm: async () => {
                    await basketQuery.refetch({ clear: true });
                    setAnotherFilterPeriod(start);
                    window.scrollTo(0, 0);
                    closeLastOverlays();
                },
                onReject: () => closeLastOverlays(),
                notifyOnly: false,
                oneRowButtons: true,
            };

            pushDialogOverlay('clear_basket_dialog_desktop_filters', dialogData);
        } else {
            setAnotherFilterPeriod(start);
            window.scrollTo(0, 0);
        }
    };

    handleAddCategory = ({ id }) => {
        const {
            selectedFilters: { selectedCategories },
        } = this.props;

        const categoryTitle = CATEGORY_TITLES.ru[id];
        setCategoriesReaction({ categories: selectedCategories.concat(id) });
        analyticService
            .push({
                eventName: 'Change_Filter_Item',
                itemKey: 'category',
                action: 'on',
                categoryId: id,
            })
            .push({
                eventName: 'Filter_Change_Category',
                filterValue: categoryTitle,
                eventAction: 'Change',
                eventLabel: 'Category',
            });
    };

    handleRemoveCategory = ({ id }) => {
        const {
            selectedFilters: { selectedCategories },
        } = this.props;

        const selectedCategory = selectedCategories.find((categoryId) => categoryId === id);
        const categoryTitle = selectedCategory.length ? CATEGORY_TITLES.ru[selectedCategory[0]] : null;
        setCategoriesReaction({ categories: selectedCategories.filter((cid) => cid !== id) });
        analyticService.push({
            eventName: 'Change_Filter_Item',
            itemKey: 'category',
            action: 'off',
            categoryId: id,
        })
            .push({
                eventName: 'Filter_Change_Category',
                filterValue: selectedCategory
                    ? categoryTitle
                    : null,
                eventAction: 'Change',
                eventLabel: 'Category',
            });
    };

    handleAddTag = (tag) => {
        appendTagReaction({ id: tag.id, isUserChangesTags: true });

        analyticService
            .push({
                eventName: 'Filter_Change_Tags',
                filterValue: tag.title,
                eventAction: 'Change',
                eventLabel: 'Tags',
            });
        window.scrollTo(0, 0);
    };

    handleRemoveTag = (tag) => {
        deleteTagReaction({ tagId: tag.id, isUserChangesTags: true });
        analyticService
            .push({
                eventName: 'Filter_Change_Tags',
                filterValue: tag.title,
                eventAction: 'Change',
                eventLabel: 'Tags',
            });
        window.scrollTo(0, 0);
    };

    handleResetFilters = () => {
        analyticService.push({
            eventName: 'Change_Filter_Item',
            itemKey: 'clear',
        });
        resetSelectedFiltersReaction();
    };

    handleExpandFilterItem = ({ isExpanded }) => {
        if (isExpanded) {
            analyticService.push({
                eventName: 'Change_Filter_Item',
                itemKey: 'openWeek',
            });
        }
    };

    renderFilters = () => {
        const {
            menuDatesContext: {
                state:
                {
                    selectedDate,
                    filterPeriod: menuDatesFilterPeriod,
                    isSelectedDateInEditableRange,
                },
            },
            menuDishesQuery: { allDishes, basketDishes },
            selectedFilters: {
                selectedPeriod,
                selectedCategoryId,
                selectedCategories,
                selectedTags,
                isSelectedDefaultFilters,
            },
            subscriptionTypeQuery: {
                subscriptionType,
            },
            isCommentEditEnabled,
            sessionCommentQuery: { viewCustomizationComment },
            isNeededRenderPeriod,
            isNeededRenderSubscription,
            isCommentAnimationDisabled,
            localeContext: { locale },
            filterPeriod,
            filterPeriods,
            locationKey,
        } = this.props;

        // Locale content data
        const {
            categoriesTitle, proteinsTitle, stylesTitle, ingredientsTitle,
        } = locales[locale];

        if (!selectedPeriod || !selectedCategoryId) return null;
        // const { periods } = menuFilter;

        const { categories, tags: periodTags } = filterPeriod;

        const tags = periodTags.filter((tag) => {
            if (tag.id === PREMIUM_FILTER_ID) {
                return IS_PREMIUM_FILTER_ENABLED;
            }
            if (tag.id === NY_DISHES) {
                return false;
            }
            return true;
        });

        const canCustomizeMenu = menuDatesFilterPeriod === selectedPeriod ? isSelectedDateInEditableRange : true;

        const categoriesData = {
            key: 'categories-list',
            title: categoriesTitle,
            options: mapCategories(categories, selectedCategories, allDishes, selectedTags, basketDishes, locale, locationKey),
            onAdd: this.handleAddCategory,
            onRemove: this.handleRemoveCategory,
        };

        const ingredientsData = {
            key: INGREDIENTS_ID,
            title: ingredientsTitle,
            options: mapTags(
                tags, [PROTEINS_ID, INGREDIENTS_ID], allDishes, selectedCategories, selectedTags, basketDishes, canCustomizeMenu,
            ),
            onAdd: this.handleAddTag,
            onRemove: this.handleRemoveTag,
        };
        const stylesData = {
            key: STYLE_ID,
            title: <Responsive desktop={stylesTitle.short} wideDesktop={stylesTitle.long} />,
            options: mapTags(tags, [STYLE_ID], allDishes, selectedCategories, selectedTags, basketDishes, canCustomizeMenu),
            onAdd: this.handleAddTag,
            onRemove: this.handleRemoveTag,
        };

        const menuFiltersData = [
            categoriesData,
            ingredientsData,
            stylesData,
        ];

        const isCommentAnimationRunning = (
            !isCommentAnimationDisabled
            && !(viewCustomizationComment && viewCustomizationComment.comment)
        );

        return (
            <>
                <CityFilter
                    selectedDate={selectedDate}
                    menuDatesFilterPeriod={menuDatesFilterPeriod}
                    periods={filterPeriods.filter((p) => p.isVisibleInFilter === 'visible')}
                    onSelect={this.handleSetPeriod}
                    selectedPeriod={selectedPeriod}
                    itemKey="period"
                    onExpand={this.handleExpandFilterItem}
                    locale={locale}
                />
                {isNeededRenderPeriod && (
                    <PeriodFilter
                        selectedDate={selectedDate}
                        menuDatesFilterPeriod={menuDatesFilterPeriod}
                        periods={filterPeriods.filter((p) => p.isVisibleInFilter === 'visible')}
                        onSelect={this.handleSetPeriod}
                        selectedPeriod={selectedPeriod}
                        itemKey="period"
                        onExpand={this.handleExpandFilterItem}
                        locale={locale}
                    />
                )}
                <MenuFilters
                    isCommentEditEnabled={isCommentEditEnabled}
                    isCommentAnimationRunning={isCommentAnimationRunning}
                    isResetButtonVisible={!isSelectedDefaultFilters}
                    canCustomizeMenu={canCustomizeMenu}
                    filtersData={menuFiltersData}
                    onExpandSection={this.handleExpandFilterItem}
                    onClickResetButton={this.handleResetFilters}
                    locale={locale}
                />
                <div styleName="discount">
                    {isNeededRenderSubscription && (
                        <SubscriptionSwitch
                            locale={locale}
                        />
                    )}
                </div>
            </>
        );
    };

    render() {
        const {
            onToggle,
            isFiltersOpen,
            overlayIsOpen,
            localeContext: { locale },
        } = this.props;

        // Locale content data
        const {
            errorText,
            filterButtonText,
        } = locales[locale];

        const scrollTopPx = getScrollTopPx();

        const classes = classNames({
            'filters-wrap filters-wrap--tablet': true,
            open: isFiltersOpen,
        });
        const buttonClasses = classNames({
            'filters-button': true,
            open: isFiltersOpen,
        });

        const wideDesktopClasses = classNames({
            'filters-wrap': true,
            // fullyScrolled: this.state.isFullyScrolled,
        });

        return (
            <div styleName="filters" ref={this.filtersRef}>
                <Responsive
                    desktop={(
                        <>
                            {/* eslint-disable-next-line */}
                            <div
                                aria-label="Открыть фильтры"
                                styleName={buttonClasses}
                                onClick={onToggle}
                                data-test-id="filters-button"
                            >
                                <div styleName="filters-button-text">{filterButtonText}</div>
                            </div>
                            <div styleName={classes}>
                                {this.renderFilters()}
                            </div>
                            {isFiltersOpen && (
                                <ScrollLock />
                            )}
                        </>
                    )}
                    wideDesktop={(
                        <div
                            ref={this.wrapContentRef}
                            styleName={wideDesktopClasses}
                        // style={overlayIsOpen ? { top: `${scrollTopPx}px` } : {}}
                        >
                            {this.renderFilters()}
                        </div>
                    )}
                />
                <div ref={this.sentinelRef} style={{ position: 'absolute', bottom: 0, height: 1 }} />
            </div>
        );
    }
}


Filters.propTypes = {
    overlayIsOpen: PropTypes.bool,
    localeContext: PropTypes.shape({
        locale: PropTypes.string,
    }).isRequired,

    menuDatesContext: PropTypes.shape({
        state: PropTypes.shape({
            selectedDate: PropTypes.string.isRequired,
            filterPeriod: PropTypes.string.isRequired,
            isSelectedDateInEditableRange: PropTypes.bool.isRequired,
        }).isRequired,
    }).isRequired,

    filterQuery: PropTypes.shape({
        loading: PropTypes.bool,
        error: PropTypes.object,
        menuFilter: PropTypes.shape({
            periods: PropTypes.arrayOf(PropTypes.shape({})),
        }),
    }).isRequired,

    selectedFilters: PropTypes.shape({
        selectedCategories: PropTypes.arrayOf(
            PropTypes.string,
        ),
        selectedPeriod: PropTypes.string,
        selectedCategoryId: PropTypes.string,
        selectedTags: PropTypes.arrayOf(
            PropTypes.string,
        ),
    }).isRequired,

    menuDishesQuery: PropTypes.shape({
        menuDishes: PropTypes.arrayOf(PropTypes.shape({})),
    }).isRequired,

    sessionCommentQuery: PropTypes.shape({
        viewCustomizationComment: PropTypes.shape({
            comment: PropTypes.string,
        }),
    }).isRequired,

    subscriptionTypeQuery: PropTypes.shape({
        subscriptionType: PropTypes.string.isRequired,
    }).isRequired,

    updateBilling: PropTypes.func.isRequired,

    addItemsToCartByPeriod: PropTypes.func.isRequired,

    basketQuery: PropTypes.shape({
        refetch: PropTypes.func,
        cart: PropTypes.shape({}),
    }),

    paymentMethod: PropTypes.shape({
        paymentMethod: PropTypes.string,
    }).isRequired,

    isCommentEditEnabled: PropTypes.bool,
    isCommentAnimationDisabled: PropTypes.bool,
    isNeededRenderPeriod: PropTypes.bool,
    isNeededRenderSubscription: PropTypes.bool,
    isFiltersOpen: PropTypes.bool,

    onClose: PropTypes.func,
    onToggle: PropTypes.func,

    dispatchOverlayContext: PropTypes.shape({
        pushDialogOverlay: PropTypes.func.isRequired,
        closeLastOverlays: PropTypes.func.isRequired,
    }).isRequired,
};

const FiltersDataAdapter = (props) => {
    const {
        overlayIsOpen = false,
        isCommentEditEnabled = true,
        isCommentAnimationDisabled = false,
        isNeededRenderPeriod = true,
        isNeededRenderSubscription = true,
        isFiltersOpen = false,
        onClose = () => { },
        onToggle = () => { },
    } = props;

    const { basketQuery } = useBasketQueryAdapter();

    const menuDatesContext = useContext(menuDatesState);
    const abTestData = useContext(abTestDataContext);
    const localeContext = useContext(LocaleContext);
    const dispatchOverlayContext = useContext(OverlayContextDispatch);
    const { filterQuery } = useContext(FiltersContext);
    const locationKey = useCategoryLocationKey();

    const [addItemsToCartByPeriod] = useMutation(ADD_ITEMS_TO_CART_BY_PERIOD);

    const filterPeriod = useSpecificPeriodData();
    const { periods: filterPeriods } = useMenuPeriodsData();


    /* HANDLER */

    const setAnotherFilterPeriod = (period) => {
        setAnoterPeriodHandler(period);
    };

    return (
        <Filters
            {...props}
            filterQuery={filterQuery}
            localeContext={localeContext}
            menuDatesContext={menuDatesContext}
            abTestDataContext={abTestData}
            dispatchOverlayContext={dispatchOverlayContext}
            locationKey={locationKey}
            overlayIsOpen={overlayIsOpen}
            isCommentEditEnabled={isCommentEditEnabled}
            isCommentAnimationDisabled={isCommentAnimationDisabled}
            isNeededRenderPeriod={isNeededRenderPeriod}
            isNeededRenderSubscription={isNeededRenderSubscription}
            isFiltersOpen={isFiltersOpen}
            onClose={onClose}
            onToggle={onToggle}
            basketQuery={basketQuery}
            addItemsToCartByPeriod={addItemsToCartByPeriod}
            filterPeriod={filterPeriod}
            filterPeriods={filterPeriods}
            setAnotherFilterPeriod={setAnotherFilterPeriod}
        />
    );
};

export default connect(
    compose(
        graphql(VIEW_CUSTOMIZATION_COMMENT, { name: 'sessionCommentQuery' }),
    )(FiltersDataAdapter),
);
